import React, { useCallback, useEffect, useMemo } from 'react';
import { type MultiPageVideoModel } from '@bynder-studio/render-core';
import { type PlaybackManipulationRenderer } from '@bynder-studio/render-web';
import { Tooltip } from '@bynder/design-system';
import { IconPause, IconPlay, IconSkipNext, IconSkipPrevious } from '@bynder/icons';
import usePlaybackShortcuts from 'packages/hooks/shortcuts/usePlaybackShortcuts';
import useEditor from 'packages/web/src/hooks/useEditor';
import VolumeControl from 'packages/pages/components/VolumeControl';
import { addZeroIfNeeded, convertFrameToSecMinFrame } from '../helpers/frame-to-time-converter';
import useForceUpdate from '../../../../hooks/useForceUpdate';
import {
    TimelineButton,
    TimelineHeaderBtns,
    TimelineHeaderContainer,
    TimelineHeaderTracker,
} from './PlaybackControls.styled';
import { parseShortcutForTooltip } from 'packages/common/WithShortcutLabel';

const convertFrameToTime = (frame: number, frameRate: number) => {
    const convertedTime = convertFrameToSecMinFrame(frame, frameRate);

    return `${addZeroIfNeeded(convertedTime.minutes)}:${addZeroIfNeeded(convertedTime.seconds)}:${addZeroIfNeeded(
        convertedTime.frame,
    )}`;
};

const PlaybackControls = ({ controlShortcutMode }) => {
    const forceUpdate = useForceUpdate();
    const { creativeModel, manipulationRenderer, frameRate } = useEditor<
        MultiPageVideoModel,
        PlaybackManipulationRenderer
    >();
    const duration = creativeModel?.getPlaybackDuration()?.getDuration() || 0;
    const currentFrame = manipulationRenderer?.getCurrentFrame() || 0;
    const isPlaying = manipulationRenderer?.getIsPlaying() || false;

    usePlaybackShortcuts({
        renderer: manipulationRenderer,
        editorType: 'EDITOR',
        controlShortcutMode,
        duration,
    });
    const convertedDuration = useMemo(() => convertFrameToTime(duration, frameRate), [duration, frameRate]);

    const togglePlayback = useCallback(() => {
        if (!manipulationRenderer) {
            return;
        }

        if (isPlaying) {
            manipulationRenderer.pausePlayback();
        } else {
            manipulationRenderer.startPlayback();
        }

        forceUpdate();
    }, [manipulationRenderer, isPlaying, forceUpdate]);

    const toFirstFrame = useCallback(() => {
        if (!manipulationRenderer || manipulationRenderer.getCurrentFrame() <= 0) {
            return;
        }

        manipulationRenderer.setCurrentFrame(0);
    }, [manipulationRenderer]);

    const toLastFrame = useCallback(() => {
        if (!manipulationRenderer || manipulationRenderer.getCurrentFrame() >= duration) {
            return;
        }

        manipulationRenderer.setCurrentFrame(duration - 1);
    }, [manipulationRenderer, duration]);

    const stopPlaybackOnFocus = useCallback(
        (e) => {
            if (!e.target.closest('.right--previewerSidebar') || !manipulationRenderer.getIsPlaying()) {
                return;
            }

            manipulationRenderer.pausePlayback();
        },
        [manipulationRenderer],
    );

    useEffect(() => {
        if (!manipulationRenderer) {
            return;
        }

        // Throttle frameUpdate to ~10fps for the timecode display (canvas stays at 60fps)
        let lastUpdateTime = 0;
        const THROTTLE_MS = 40; // 1000ms (1s) / 25 fram = 40ms
        const throttledFrameUpdate = () => {
            const now = Date.now();

            if (now - lastUpdateTime >= THROTTLE_MS) {
                lastUpdateTime = now;
                forceUpdate();
            }
        };

        manipulationRenderer.eventEmitter.on('frameUpdate', throttledFrameUpdate);
        manipulationRenderer.eventEmitter.on('playing', forceUpdate);
        manipulationRenderer.eventEmitter.on('paused', forceUpdate);

        return () => {
            manipulationRenderer.eventEmitter.off('frameUpdate', throttledFrameUpdate);
            manipulationRenderer.eventEmitter.off('playing', forceUpdate);
            manipulationRenderer.eventEmitter.off('paused', forceUpdate);
        };
    }, [manipulationRenderer, forceUpdate]);

    useEffect(() => {
        if (!manipulationRenderer) {
            return;
        }

        window.addEventListener('mousedown', stopPlaybackOnFocus);

        return () => {
            window.removeEventListener('mousedown', stopPlaybackOnFocus);
        };
    }, [manipulationRenderer]);

    return (
        <TimelineHeaderContainer>
            <TimelineHeaderTracker>
                <span>{convertFrameToTime(currentFrame, frameRate)}</span>
                {' / '}
                {convertedDuration}
            </TimelineHeaderTracker>
            <TimelineHeaderBtns>
                <Tooltip content={`Skip to beginning (${parseShortcutForTooltip(['shift', 'cmd', '←'])})`}>
                    <TimelineButton
                        name="previousButton"
                        disabled={currentFrame === 0}
                        onClick={toFirstFrame}
                        onMouseDown={(e) => e.preventDefault()}
                    >
                        <IconSkipPrevious />
                    </TimelineButton>
                </Tooltip>
                <Tooltip content={`Play/Pause (${parseShortcutForTooltip(['space'])})`}>
                    <TimelineButton name="playButton" onClick={togglePlayback} onMouseDown={(e) => e.preventDefault()}>
                        {isPlaying ? <IconPause /> : <IconPlay />}
                    </TimelineButton>
                </Tooltip>
                <Tooltip content={`Skip to end (${parseShortcutForTooltip(['shift', 'cmd', '→'])})`}>
                    <TimelineButton
                        name="nextButton"
                        disabled={currentFrame >= duration - 1}
                        onClick={toLastFrame}
                        onMouseDown={(e) => e.preventDefault()}
                    >
                        <IconSkipNext />
                    </TimelineButton>
                </Tooltip>
            </TimelineHeaderBtns>
            <VolumeControl creativeModel={creativeModel} size="s" />
        </TimelineHeaderContainer>
    );
};

export default PlaybackControls;
